import type { TFunction } from 'i18next'
import type { HybridSearchPayloadWithPrice } from '~/shared/interfaces'

import { getProgrammeProgress } from "/app/libraries/arcana/src/blocks/Cards/ProgrammeCard/index.ts"

import { MediaTypes } from '@setplex/tria-api'
import { formatDateDuration, timeWithin } from '~/shared/helpers/date'

/** Watch Now counterpart of `SEARCH_CARD_KIND_CATCHUP` (FP-4241) */
export const SEARCH_CARD_KIND_WATCH_NOW = 'EPG_WATCH_NOW'

interface WatchNowCardFields {
  cardKind?: string
  watchNowProgrammeTitle?: string
  programmeProgress?: number | null
  watchNowTimeLabel?: string
}

/** A channel the backend put in Watch Now, with or without EPG data */
function isWatchNowChannel(item: HybridSearchPayloadWithPrice): boolean {
  return (
    item.section === 'WATCH_NOW' && item.contentType === MediaTypes.TV_CHANNEL
  )
}

/**
 * Whether a Watch Now channel is airing a programme right now (AC-3).
 *
 * A channel matched by name without EPG data, or one whose programme has
 * already ended, is not treated as live — the Live channel card is used
 * instead of a programme card with stale or empty EPG fields (FP-4297, AC-10).
 */
export function isWatchNowLiveProgramme(
  item: HybridSearchPayloadWithPrice,
  nowMs: number
): boolean {
  if (!isWatchNowChannel(item)) return false

  const { programmeTitle, startMs, stopMs } = item.programme ?? {}
  if (!programmeTitle || startMs == null || stopMs == null) return false
  if (!Number.isFinite(startMs) || !Number.isFinite(stopMs)) return false

  return startMs <= nowMs && nowMs < stopMs
}

/** Remaining airtime of a live programme, e.g. "5m left" (UI-5.7) */
export function formatRemainingTimeLabel({
  stopMs,
  nowMs,
  t,
}: {
  stopMs?: number
  nowMs: number
  t: TFunction
}): string {
  if (stopMs == null || !Number.isFinite(stopMs) || stopMs <= nowMs) return ''

  const { hours, minutes } = timeWithin(stopMs - nowMs)
  // A programme in its last seconds has no duration to format, which would
  // leave a bare "left" on the card
  if (!hours && !minutes) return ''

  return t('time_left', {
    time: formatDateDuration({ duration: { hours, minutes }, short: true }),
  })
}

/**
 * Card fields for an on-air Watch Now programme; empty otherwise, which keeps
 * the kind resolved by section and content type (FP-4241 `getSearchCardKind`).
 *
 * Only an on-air programme overrides that kind with the Watch Now programme
 * card. Without EPG data — or with a programme that is no longer on air — the
 * Live channel card is used instead (FP-4297). The programme title is passed
 * through here rather than read off `programme` in the skin, so a programme
 * that is no longer on air cannot leave a stale title on the card (AC-10).
 *
 * Progress and the remaining-time label are a snapshot taken when the results
 * arrive: this is a search result list, not a live EPG grid.
 */
export function toWatchNowCardFields(
  item: HybridSearchPayloadWithPrice,
  nowMs: number,
  t: TFunction
): WatchNowCardFields {
  if (!isWatchNowLiveProgramme(item, nowMs)) return {}

  const { programmeTitle, startMs, stopMs } = item.programme!

  return {
    cardKind: SEARCH_CARD_KIND_WATCH_NOW,
    watchNowProgrammeTitle: programmeTitle,
    programmeProgress: getProgrammeProgress({ startMs, stopMs, nowMs }),
    watchNowTimeLabel: formatRemainingTimeLabel({ stopMs, nowMs, t }),
  }
}
